import axios from "axios";
import { getTenantId } from "@/lib/storeManager";

const userApiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

userApiClient.interceptors.request.use(config => {
  const tenantId = getTenantId();
  if (tenantId && config.headers) {
    config.headers["X-Tenant-Id"] = tenantId;
  }
  return config;
});

export interface InstitutionListItem {
  id?: string | number;
  name?: string;
  institution_name?: string;
  city?: string;
  district?: string;
  state?: string;
  address?: string;
  address_line1?: string;
  pincode?: string;
  type?: string;
  institution_type?: string;
  total_students?: number;
  capacity?: number;
  sic_rating?: number;
  scholarship_available?: boolean;
  scholarship?: boolean;
  [key: string]: unknown;
}

export interface InstitutionDetailItem extends InstitutionListItem {}

const toInstitutionList = (payload: unknown): InstitutionListItem[] => {
  if (Array.isArray(payload)) {
    return payload as InstitutionListItem[];
  }
  if (!payload || typeof payload !== "object") {
    return [];
  }

  const data = payload as Record<string, unknown>;
  if (Array.isArray(data.results)) {
    return data.results as InstitutionListItem[];
  }
  if (Array.isArray(data.data)) {
    return data.data as InstitutionListItem[];
  }
  if (Array.isArray(data.items)) {
    return data.items as InstitutionListItem[];
  }

  return [];
};

const toInstitutionDetail = (payload: unknown): InstitutionDetailItem => {
  if (!payload || typeof payload !== "object") {
    return {} as InstitutionDetailItem;
  }

  const data = payload as Record<string, unknown>;

  if (data.data && typeof data.data === "object") {
    return data.data as InstitutionDetailItem;
  }

  if (data.result && typeof data.result === "object") {
    return data.result as InstitutionDetailItem;
  }

  return data as InstitutionDetailItem;
};

export const publicInstitutionsAPI = {
  getInstitutionsList: async (
    params?: Record<string, unknown> | URLSearchParams
  ) => {
    let finalParams: any = params;
    if (!(params instanceof URLSearchParams)) {
      finalParams = { ...params, _: Date.now() };
    }
    const response = await userApiClient.get("/jbf/institutions-list/", {
      params: finalParams,
    });
    return toInstitutionList(response.data);
  },

  getInstitutionDetail: async (id: string) => {
    const response = await userApiClient.get<InstitutionDetailItem>(
      `/jbf/institutions-list/${id}/`
    );
    return toInstitutionDetail(response.data);
  },

  /** GET /jbf/states/ — list states for filter dropdowns */
  getStates: async (): Promise<string[]> => {
    const response = await userApiClient.get("/jbf/states/");
    const raw = response.data;
    const data =
      (raw as Record<string, unknown>).data ??
      (raw as Record<string, unknown>).results ??
      raw;
    if (Array.isArray(data)) {
      return data
        .map((item: unknown) =>
          typeof item === "string"
            ? item
            : ((item as { name?: string })?.name ?? "")
        )
        .filter(Boolean)
        .sort();
    }
    return [];
  },

  /** GET /jbf/cities/ — list cities for filter dropdowns */
  getCities: async (state?: string): Promise<string[]> => {
    const response = await userApiClient.get("/jbf/cities/", {
      params: state ? { state } : undefined,
    });
    const raw = response.data;
    const data =
      (raw as Record<string, unknown>).data ??
      (raw as Record<string, unknown>).results ??
      raw;
    if (Array.isArray(data)) {
      return data
        .map((item: unknown) =>
          typeof item === "string"
            ? item
            : ((item as { name?: string })?.name ?? "")
        )
        .filter(Boolean)
        .sort();
    }
    return [];
  },
};

// ============================================================================
// Donations API (Razorpay flow: create-order -> checkout -> verify-payment)
// ============================================================================

export interface DonationCreateOrderPayload {
  name: string;
  email: string;
  phone: string;
  donation_type: string;
  amount: string;
  organization?: string;
  pan_number?: string;
  aadhaar_number?: string;
  message?: string;
}

export interface DonationCreateOrderResponse {
  order_id: string;
  amount: number;
  currency?: string;
  key?: string;
  key_id?: string;
}

/** Backend create-order raw response: { message, status_code, data: { order_id, amount, ... } } */
interface DonationCreateOrderApiResponse {
  message?: string;
  status_code?: number;
  data?: {
    donation_id?: number;
    order_id?: string;
    amount?: number;
    currency?: string;
    key_id?: string;
  };
}

/** Payload for verify-payment: exactly these three values from Razorpay checkout success response */
export interface DonationVerifyPaymentPayload {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export const donationsAPI = {
  /** POST /jbf/donations/create-order/ — create Razorpay order; returns order_id and amount (paise) */
  createOrder: async (
    payload: DonationCreateOrderPayload
  ): Promise<DonationCreateOrderResponse> => {
    const { data } = await userApiClient.post<DonationCreateOrderApiResponse>(
      "/jbf/donations/create-order/",
      payload
    );
    const inner = data?.data;
    return {
      order_id: inner?.order_id ?? "",
      amount: inner?.amount ?? 0,
      currency: inner?.currency,
      key_id: inner?.key_id,
    };
  },

  /** POST /jbf/donations/verify-payment/ — verify after Razorpay success; payload: razorpay_order_id, razorpay_payment_id, razorpay_signature */
  verifyPayment: async (
    payload: DonationVerifyPaymentPayload
  ): Promise<unknown> => {
    const body: DonationVerifyPaymentPayload = {
      razorpay_order_id: payload.razorpay_order_id,
      razorpay_payment_id: payload.razorpay_payment_id,
      razorpay_signature: payload.razorpay_signature,
    };
    const { data } = await userApiClient.post<unknown>(
      "/jbf/donations/verify-payment/",
      body
    );
    return data;
  },
};

// ============================================================================
// Blogs API (public list for user portal)
// ============================================================================

export interface BlogListItem {
  id: number;
  title: string;
  sub_title: string | null;
  description: string | null;
  category: number;
  category_name: string | null;
  date: string | null;
  is_enabled: boolean;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  banner: string | null;
  video: string | null;
}

export interface BlogListResponse {
  message: string;
  status_code: number;
  data: BlogListItem[];
}

export interface BlogDetailItem {
  id: number;
  title: string;
  sub_title: string | null;
  description: string | null;
  category: number;
  category_name: string | null;
  date: string | null;
  is_enabled: boolean;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  banner: string | null;
  video: string | null;
}

export const blogsAPI = {
  /** GET /jbf/blogs/ — list blogs. Cache-busted via query param only (no custom headers) to avoid CORS preflight. */
  getBlogList: async (): Promise<BlogListItem[]> => {
    const response = await userApiClient.get<BlogListResponse>("/jbf/blogs/", {
      params: { _: Date.now() },
    });
    const data = response.data?.data;
    return Array.isArray(data) ? data : [];
  },

  getBlogDetail: async (id: string): Promise<BlogDetailItem> => {
    const response = await userApiClient.get<{
      message?: string;
      status_code?: number;
      data?: BlogDetailItem;
    }>(`/jbf/blogs/${id}/`);
    const body = response.data as
      | { data?: BlogDetailItem }
      | BlogDetailItem
      | null
      | undefined;
    if (body && typeof body === "object" && "data" in body && body.data) {
      return body.data;
    }
    return (body ?? {}) as BlogDetailItem;
  },
};

// ============================================================================
// Vendors API (public list & detail for user portal)
// ============================================================================

export interface VendorListItem {
  id: number;
  logo: string | null;
  vendor_name: string;
  category: string | string[]; // API may send JSON string or array
  company_name: string | null;
  vendor_type: string | null;
  short_description: string | null;
  city: string | null;
  state: string | null;
  pin_code: string | null;
  verification_status: string | null;
  available_for_work?: boolean | null;
  [key: string]: unknown;
}

export interface VendorDetailItem extends VendorListItem {
  contact_person_name?: string | null;
  mobile_number?: string | null;
  email?: string | null;
  office_address?: string | null;
  service_areas?: string | string[] | null;
  pan_india_service?: boolean | null;
  services_offered?: string | string[] | null;
  specialization?: string | null;
  years_of_experience?: number | null;
  hostel_projects_experience?: boolean | null;
  past_projects?: string | string[] | null;
  empanelment_status?: string | null;
  compliance_confirmation?: boolean | null;
  admin_notes?: string | null;
  working_days?: string | string[] | null;
  service_timings?: string | null;
  documents_uploaded?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

function parseVendorCategory(
  value: string | string[] | null | undefined
): string {
  if (value === null) {
    return "—";
  }
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "—";
  }
  if (typeof value !== "string") {
    return "—";
  }
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed) ? parsed.join(", ") : value;
  } catch {
    return value || "—";
  }
}

export interface VendorListResponse {
  data: VendorListItem[];
  count: number;
}

export const vendorsAPI = {
  getVendorList: async (params?: {
    page?: number;
    page_size?: number;
  }): Promise<VendorListResponse> => {
    const search = new URLSearchParams();
    if (params?.page !== null) {
      search.set("page", String(params?.page ?? 1));
    }
    if (params?.page_size !== null) {
      search.set("page_size", String(params?.page_size ?? 0));
    }
    search.set("_", String(Date.now())); // disk cache bust
    const query = search.toString();
    const url = `/jbf/vendors/?${query}`;
    const response = await userApiClient.get<{
      message?: string;
      status_code?: number;
      count?: number;
      data?: VendorListItem[];
    }>(url);
    const body = response.data;
    if (!body || typeof body !== "object") {
      return { data: [], count: 0 };
    }
    const data = Array.isArray((body as { data?: VendorListItem[] }).data)
      ? (body as { data: VendorListItem[] }).data
      : [];
    const count =
      typeof (body as { count?: number }).count === "number"
        ? (body as { count: number }).count
        : data.length;
    return { data, count };
  },

  getVendorDetail: async (id: string): Promise<VendorDetailItem> => {
    const response = await userApiClient.get<{
      message?: string;
      status_code?: number;
      data?: VendorDetailItem;
    }>(`/jbf/vendors/${id}/`);
    const body = response.data as
      | { data?: VendorDetailItem }
      | VendorDetailItem
      | null
      | undefined;
    if (body && typeof body === "object" && "data" in body && body.data) {
      return body.data as VendorDetailItem;
    }
    if (body && typeof body === "object" && "id" in body) {
      return body as VendorDetailItem;
    }
    throw new Error("Vendor detail not found");
  },
};

// ============================================================================
// FAQs & Testimonials API (public)
// ============================================================================

export interface FAQQuestion {
  id: number;
  category: number;
  category_title: string;
  question: string;
  answer: string;
  is_enabled: boolean;
  order_by: number;
  created_at?: string;
  updated_at?: string;
}

export interface FAQCategory {
  id: number | string;
  title: string;
  questions: FAQQuestion[];
}

export interface Testimonial {
  id: number;
  category: string;
  name: string;
  designation: string;
  description: string;
  is_enabled: boolean;
  order_by: number;
  created_at?: string;
  updated_at?: string;
}

export const faqAPI = {
  getFAQList: async (): Promise<FAQQuestion[]> => {
    const response = await userApiClient.get<{ data: FAQQuestion[] }>(
      "/jbf/faq-questions/",
      {
        params: { _: Date.now() },
      }
    );
    const data = response.data?.data;
    return Array.isArray(data) ? data.filter(q => q.is_enabled) : [];
  },
};

export const testimonialsAPI = {
  getTestimonialList: async (): Promise<Testimonial[]> => {
    const response = await userApiClient.get<{ data: Testimonial[] }>(
      "/jbf/testimonials/",
      {
        params: { _: Date.now() },
      }
    );
    const data = response.data?.data;
    return Array.isArray(data) ? data.filter(t => t.is_enabled) : [];
  },
};

export interface ContactFormPayload {
  name: string;
  email: string;
  phone_number: string;
  subject: string;
  message: string;
}

export const contactFormAPI = {
  submit: async (payload: ContactFormPayload): Promise<void> => {
    await userApiClient.post("/jbf/contact-forms/", payload);
  },
};

// ============================================================================
// Events API (Public List)
// ============================================================================

export interface JBFEvent {
  id: number | string;
  event_for: string;
  address: string;
  event_title: string;
  date: string;
  number_of_days: number | string;
  description: string;
  created_at?: string;
  updated_at?: string;
}

export const eventsAPI = {
  /** GET /jbf/events/ — list all events. */
  getEventList: async (): Promise<JBFEvent[]> => {
    const response = await userApiClient.get<{ data: JBFEvent[] }>(
      "/jbf/events/",
      {
        params: { _: Date.now() },
      }
    );
    const data = response.data?.data;
    return Array.isArray(data) ? data : [];
  },
};

// ============================================================================
// Media Gallery API (Public List)
// ============================================================================

export interface GalleryCategory {
  id: number | string;
  name: string;
  display_order: number;
  is_enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface GalleryItem {
  id: number | string;
  category: number | string;
  category_name?: string;
  title: string;
  description: string;
  year: string | number;
  display_order: number;
  image?: string;
  created_at?: string;
  updated_at?: string;
}

export const galleryCategoriesAPI = {
  getCategoryList: async (): Promise<GalleryCategory[]> => {
    const response = await userApiClient.get<{ data: GalleryCategory[] }>(
      "/jbf/gallery-categories/",
      {
        params: { _: Date.now() },
      }
    );
    const data = response.data?.data;
    const items = Array.isArray(data)
      ? data
      : Array.isArray(response.data)
        ? response.data
        : [];
    return (items as GalleryCategory[]).filter(c => c.is_enabled);
  },
};

export const galleryAPI = {
  getGalleryList: async (): Promise<GalleryItem[]> => {
    const response = await userApiClient.get<{ data: GalleryItem[] }>(
      "/jbf/gallery/",
      {
        params: { _: Date.now() },
      }
    );
    const data = response.data?.data;
    const items = Array.isArray(data)
      ? data
      : Array.isArray(response.data)
        ? response.data
        : [];
    return items as GalleryItem[];
  },
};

export { parseVendorCategory };
